import React, { useEffect, useState } from 'react';
import { Box, LinearProgress, Typography, Fade, Grow } from '@mui/material';
import { keyframes } from '@mui/system';

// Pulse animation for when progress increases
const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(25, 118, 210, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(25, 118, 210, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(25, 118, 210, 0);
  }
`;

// Glow animation for completion
const glow = keyframes`
  0% {
    box-shadow: 0 0 5px rgba(76, 175, 80, 0.5);
  }
  50% {
    box-shadow: 0 0 20px rgba(76, 175, 80, 0.8);
  }
  100% {
    box-shadow: 0 0 5px rgba(76, 175, 80, 0.5);
  }
`;

export default function AnimatedProgress({ 
  value, 
  color = 'primary', 
  height = 8, 
  showLabel = false,
  animate = true,
  variant = 'determinate'
}) {
  const [displayValue, setDisplayValue] = useState(value);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    // Animate value changes
    if (animate && value !== displayValue) {
      setIsAnimating(true);
      const duration = 1000; // 1 second animation
      const steps = 20;
      const stepValue = (value - displayValue) / steps;
      let currentStep = 0;

      const interval = setInterval(() => {
        currentStep++;
        if (currentStep >= steps) {
          setDisplayValue(value);
          clearInterval(interval);
          setTimeout(() => setIsAnimating(false), 300);
          
          // Check if complete
          if (value === 100) {
            setIsComplete(true);
            setTimeout(() => setIsComplete(false), 2000);
          }
        } else {
          setDisplayValue(prev => prev + stepValue);
        }
      }, duration / steps);

      return () => clearInterval(interval);
    } else {
      setDisplayValue(value);
    }
  }, [value, animate]);

  return (
    <Box sx={{ width: '100%', position: 'relative' }}>
      <LinearProgress
        variant={variant}
        value={displayValue}
        color={color}
        sx={{
          height,
          borderRadius: height / 2,
          backgroundColor: 'action.hover',
          '& .MuiLinearProgress-bar': {
            borderRadius: height / 2,
            transition: 'transform 0.4s ease',
            ...(isAnimating && {
              animation: `${pulse} 1.5s ease-in-out`,
            }),
            ...(isComplete && {
              animation: `${glow} 2s ease-in-out`,
              backgroundColor: 'success.main',
            }),
          },
        }}
      />
      
      {showLabel && (
        <Fade in={true} timeout={500}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              pointerEvents: 'none',
            }}
          >
            <Typography 
              variant="caption" 
              sx={{ 
                fontWeight: 'bold',
                color: displayValue > 50 ? 'common.white' : 'text.primary',
                textShadow: displayValue > 50 ? '0 1px 2px rgba(0,0,0,0.3)' : 'none',
              }}
            >
              {Math.round(displayValue)}%
            </Typography>
          </Box>
        </Fade>
      )}

      {isComplete && (
        <Grow in={isComplete} timeout={300}>
          <Typography
            variant="caption"
            sx={{
              position: 'absolute',
              top: -20,
              right: 0,
              color: 'success.main',
              fontWeight: 'bold',
            }}
          >
            Complete! ✓
          </Typography>
        </Grow>
      )}
    </Box>
  );
}

// Circular progress variant
export function AnimatedCircularProgress({ 
  value, 
  size = 80, 
  thickness = 4,
  color = 'primary',
  showLabel = true,
  animate = true
}) {
  const [displayValue, setDisplayValue] = useState(value);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (animate && value !== displayValue) {
      setIsAnimating(true);
      const duration = 1000;
      const steps = 20;
      const stepValue = (value - displayValue) / steps;
      let currentStep = 0;

      const interval = setInterval(() => {
        currentStep++;
        if (currentStep >= steps) {
          setDisplayValue(value);
          clearInterval(interval);
          setTimeout(() => setIsAnimating(false), 300);
        } else {
          setDisplayValue(prev => prev + stepValue);
        }
      }, duration / steps);

      return () => clearInterval(interval);
    } else {
      setDisplayValue(value);
    }
  }, [value, animate]);

  const circumference = 2 * Math.PI * ((size - thickness) / 2);
  const strokeDashoffset = circumference - (displayValue / 100) * circumference;

  return (
    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={(size - thickness) / 2}
          fill="none"
          stroke="rgba(0, 0, 0, 0.1)"
          strokeWidth={thickness}
        />
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={(size - thickness) / 2}
          fill="none"
          stroke={color === 'primary' ? '#1976d2' : '#4caf50'}
          strokeWidth={thickness}
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          style={{
            transition: isAnimating ? 'stroke-dashoffset 0.5s ease' : 'none',
          }}
        />
      </svg>
      
      {showLabel && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        >
          <Typography variant="h6" component="div" color="text.secondary">
            {Math.round(displayValue)}%
          </Typography>
        </Box>
      )}
    </Box>
  );
}